import _groupBy from 'lodash/groupBy';

import {nodeTypeConfig} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {Placement_UI} from 'common/src/modules/campaignPlacements/placements.types';
import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import tabularDataToCsv from 'common/src/modules/shared/copilot/csvUtils/tabularDataToCsv';
import {IssueLevelToIssueName} from 'common/src/modules/issues/issues.config';
import {CLIssueLevel} from 'common/src/modules/issues/issues.types';
import {DisconnectedMapNode, NodeType} from 'common/src/modules/nodes/nodes.types';
import {CLStore} from 'common/src/redux/store';
import {campaignTypeNameMap} from '../../../campaigns.consts';
import getDestinationCardPhrase from './utils/nodeDetails/getDestinationCardPhrase';
import getNodeSummary from './utils/nodeDetails/getNodeSummary';

type CampaignSetupCard = {
  id: number;
  name: string;
  summary: string;
  type: NodeType;
  nextNodes: number[];
  issues: CampaignSetupCardIssue[];
};

type CampaignSetupCardIssue = {
  level: CLIssueLevel;
  message: string;
};

type CampaignSetupContext = {
  cards: CampaignSetupCard[];
};

const MAX_CARDS_TO_DISPLAY = 30;

function _getNodeOutputsSummary(node: DisconnectedMapNode): string {
  return node.outputs
    .map((output) => `${output.id} ${getDestinationCardPhrase(output.nodeId)}`)
    .join('\n');
}

function campaignSetupContext(store: CLStore): AgentContext<CampaignSetupContext> {
  function getter(): CampaignSetupContext {
    const state = store.getState();
    const issues = state.campaignPage.issues.campaignLogicIssues;
    const campaign = state.campaignPage.campaignTree.campaign;
    const campaignLogic = state.campaignPage.campaignLogic;
    const campaignNodes = campaignLogic.nodes[CampaignTreeType.Campaign];

    if (!campaign) {
      return {
        cards: [],
      };
    }

    const issuesByCardId = _groupBy(issues, 'nodeId');

    const cards: CampaignSetupCard[] = Object.values(campaignNodes).map(
      (node: DisconnectedMapNode) => {
        return {
          id: node.id,
          name:
            node.type === NodeType.CAMPAIGN
              ? 'Campaign Card'
              : node.name || nodeTypeConfig[node.type].name,
          summary:
            node.type === NodeType.CAMPAIGN
              ? `${campaignTypeNameMap[campaign.type]} campaign card`
              : getNodeSummary(node.id),
          outputs: _getNodeOutputsSummary(node),
          type: node.type,
          nextNodes: node.outputs?.map((output) => output.nodeId || 0).filter(Boolean) || [],
          issues:
            issuesByCardId[node.id]
              ?.map((issue) => ({
                level: issue.level,
                message: issue.message as string,
              }))
              .filter(Boolean) || [],
        };
      },
    );

    return {
      cards,
    };
  }

  function formatter({currentContext}: {currentContext: CampaignSetupContext}): string {
    if (!currentContext) {
      return '';
    }

    const {cards} = currentContext;

    if (cards.length === 0) {
      return 'No campaign cards are currently available for setup.';
    }

    const cardsForDisplay = cards.slice(0, MAX_CARDS_TO_DISPLAY);

    const cardSumamries = cardsForDisplay
      .map((card) =>
        card.type === NodeType.CAMPAIGN
          ? `Card ID: ${card.id} - Campaign Card`
          : getNodeSummary(card.id),
      )
      .join('\n\n');

    // Collect all issues from all cards in CSV format
    const issues = cardsForDisplay.flatMap((card) =>
      card.issues.map((issue) => ({
        cardId: card.id,
        cardName: card.name,
        issueLevel: IssueLevelToIssueName[issue.level],
        message: issue.message,
      })),
    );

    const issuesSummary = issues.length > 0 ? `${tabularDataToCsv(issues)}` : '';

    const cardsSummary =
      cards.length > MAX_CARDS_TO_DISPLAY
        ? `Campaign has ${cards.length} cards. Showing first ${MAX_CARDS_TO_DISPLAY}: \n\n ${cardSumamries}`
        : `Cards in the campaign:\n${cardSumamries}`;

    return `${cardsSummary}\n\nCard Issues: \n${issuesSummary}`;
  }

  return {
    title: 'Campaign Setup Cards',
    getter,
    formatter,
  };
}

type PlacementsSummaryContext = {
  isInCampaign: boolean;
  totalCount: number;
  countByDsp: Record<string, number>;
  placementIdExamples: string[];
  placementNameExamples: string[];
  distinctEnvironments: string[];
  customParams: Record<number, {examples: string[]; distinctCount: number}>;
};

function placementsSummaryContext(store: CLStore): AgentContext<PlacementsSummaryContext> {
  function getter(): PlacementsSummaryContext {
    const state = store.getState();
    const campaign = state.campaignPage.campaignTree.campaign;

    if (!campaign) {
      return {
        isInCampaign: false,
        totalCount: 0,
        countByDsp: {},
        placementIdExamples: [],
        placementNameExamples: [],
        distinctEnvironments: [],
        customParams: {},
      };
    }

    const placements: Placement_UI[] = state.campaignPage.campaignTree.campaignPlacements;

    const totalCount = placements.length;
    const countByDsp: Record<string, number> = {};
    const placementIdSet = new Set<string>();
    const placementNameSet = new Set<string>();
    const environmentSet = new Set<string>();
    const customParamsMap = new Map<number, Set<string>>();

    placements.forEach((placement) => {
      // Count by DSP
      const dspName = placement.dspUIName;

      if (dspName) {
        countByDsp[dspName] = (countByDsp[dspName] || 0) + 1;
      }

      // Collect placement IDs
      if (placement.placementId) {
        placementIdSet.add(placement.placementId);
      }

      // Collect placement names
      if (placement.name) {
        placementNameSet.add(placement.name);
      }

      // Collect environments
      if (placement.environmentName) {
        environmentSet.add(placement.environmentName);
      }

      // Collect custom text params
      placement.customTextParams.forEach((param) => {
        if (param.value) {
          if (!customParamsMap.has(param.type)) {
            customParamsMap.set(param.type, new Set());
          }

          const paramSet = customParamsMap.get(param.type);

          if (paramSet) {
            paramSet.add(param.value);
          }
        }
      });
    });

    // Convert to arrays and take first 3 examples
    const placementIdExamples = Array.from(placementIdSet).slice(0, 3);
    const placementNameExamples = Array.from(placementNameSet).slice(0, 3);
    const distinctEnvironments = Array.from(environmentSet);

    // Process custom params
    const customParams: Record<number, {examples: string[]; distinctCount: number}> = {};

    customParamsMap.forEach((values, type) => {
      const valuesArray = Array.from(values);

      customParams[type] = {
        examples: valuesArray.slice(0, 3),
        distinctCount: valuesArray.length,
      };
    });

    return {
      isInCampaign: true,
      totalCount,
      countByDsp,
      placementIdExamples,
      placementNameExamples,
      distinctEnvironments,
      customParams,
    };
  }

  function formatter({currentContext}: {currentContext: PlacementsSummaryContext}): string {
    if (!currentContext) {
      return '';
    }

    // Not in a campaign - return empty string
    if (!currentContext.isInCampaign) {
      return '';
    }

    // In a campaign but no placements - return message
    if (currentContext.totalCount === 0) {
      return 'Campaign currently has no placements';
    }

    const {
      totalCount,
      countByDsp,
      placementIdExamples,
      placementNameExamples,
      distinctEnvironments,
      customParams,
    } = currentContext;

    const lines: string[] = [];

    lines.push('Placements Summary:');
    lines.push(`Total Count: ${totalCount}`);

    // Per DSP
    if (Object.keys(countByDsp).length > 0) {
      lines.push('Per DSP:');

      Object.entries(countByDsp).forEach(([dspName, count]) => {
        lines.push(`  ${dspName} - ${count}`);
      });
    }

    // Placement ID examples
    if (placementIdExamples.length > 0) {
      lines.push(`Placement ID Examples: ${placementIdExamples.join(', ')}`);
    }

    // Placement name examples
    if (placementNameExamples.length > 0) {
      lines.push(`Placement Name Examples: ${placementNameExamples.join(', ')}`);
    }

    // Environments
    if (distinctEnvironments.length > 0) {
      lines.push(`Placement Environments: ${distinctEnvironments.join(', ')}`);
    }

    // Custom parameters
    Object.entries(customParams)
      .sort(([typeA], [typeB]) => Number(typeA) - Number(typeB))
      .forEach(([type, {examples, distinctCount}]) => {
        const typeNumber = Number(type);
        const displayIndex = Math.floor((typeNumber - 40) / 10);

        lines.push(
          `Custom Parameter ${displayIndex} Examples: ${examples.join(', ')} (${distinctCount} distinct values)`,
        );
      });

    return lines.join('\n');
  }

  return {
    title: 'Placements Summary',
    getter,
    formatter,
  };
}

export default campaignSetupContext;
export {placementsSummaryContext};
